import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, X, Check, Sparkles } from 'lucide-react';
import { api } from '../utils/api';

// Routes where we hide the floating button (admin, auth, payment flows, embedded pages)
const HIDDEN_PREFIXES = ['/admin', '/login', '/register', '/forgot-password', '/reset-password', '/payment', '/unsubscribed'];

export default function FloatingSubscribeButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [state, setState] = useState('idle'); // idle | sending | success | error
  const [message, setMessage] = useState('');

  const hidden = HIDDEN_PREFIXES.some(p => location.pathname === p || location.pathname.startsWith(p + '/'));

  // Escape closes modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Auto-tease: open the popup on its own after 30s on first visit (only once per browser)
  useEffect(() => {
    if (hidden) return;
    const seen = typeof window !== 'undefined' && localStorage.getItem('subscribe_tease_shown');
    if (seen) return;
    const timer = setTimeout(() => {
      setOpen(true);
      try { localStorage.setItem('subscribe_tease_shown', String(Date.now())); } catch (e) {}
    }, 30000);
    return () => clearTimeout(timer);
  }, [hidden]);

  if (hidden) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      setState('sending');
      const res = await api.post('/newsletter/subscribe', {
        email: email.trim(),
        name: name.trim() || null,
        source: 'floating_popup',
      });
      setMessage(res.data?.message || "You're in.");
      setState('success');
    } catch (err) {
      setMessage(err?.response?.data?.detail || 'Something went wrong — please try again.');
      setState('error');
    }
  };

  const reset = () => {
    setOpen(false);
    setTimeout(() => {
      setEmail('');
      setName('');
      setState('idle');
      setMessage('');
    }, 300);
  };

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(true)}
        aria-label="Subscribe to Love Restored"
        data-testid="floating-subscribe-btn"
        className="fixed z-40 rounded-full transition-all duration-300 hover:-translate-y-1 hover:scale-105 inline-flex items-center gap-2"
        style={{
          bottom: '24px',
          right: '24px',
          padding: '14px 22px',
          background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)',
          color: '#3D2E0A',
          fontFamily: 'Manrope, sans-serif',
          fontSize: '13px',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          border: '1px solid rgba(255,255,255,0.6)',
          boxShadow: '0 8px 24px rgba(201,155,46,0.45), inset 0 1px 0 rgba(255,255,255,0.5)',
        }}
      >
        <Mail className="w-4 h-4" strokeWidth={2.5} />
        <span>Subscribe</span>
      </button>

      {/* Modal */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
          style={{ background: 'rgba(44,42,41,0.5)', backdropFilter: 'blur(4px)' }}
          onClick={reset}
          data-testid="subscribe-modal"
        >
          <div
            className="w-full max-w-md rounded-3xl overflow-hidden"
            style={{ backgroundColor: '#FDFBF7', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Gold header */}
            <div
              className="p-6 relative"
              style={{ background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)' }}
            >
              <button
                onClick={reset}
                aria-label="Close"
                className="absolute top-3 right-3 p-1.5 rounded-full transition hover:bg-white/30"
                data-testid="subscribe-close"
              >
                <X className="w-4 h-4" style={{ color: '#3D2E0A' }} />
              </button>
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4" style={{ color: '#3D2E0A' }} />
                <p className="text-xs font-bold uppercase" style={{ color: '#3D2E0A', letterSpacing: '0.12em', fontFamily: 'Manrope, sans-serif' }}>
                  Stay connected
                </p>
              </div>
              <h2 className="text-2xl font-bold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C1E0A', lineHeight: 1.2 }}>
                Warm words, straight to your inbox
              </h2>
              <p className="text-sm mt-2" style={{ color: '#5C4515', fontFamily: 'Manrope, sans-serif', lineHeight: 1.6 }}>
                New articles, prayers, and quiet reminders that you're not alone. No spam — ever.
              </p>
            </div>

            {/* Body */}
            <div className="p-6">
              {state === 'success' ? (
                <div className="text-center py-6" data-testid="subscribe-success">
                  <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ background: 'linear-gradient(135deg, #A8C89A 0%, #78A18E 100%)' }}>
                    <Check className="w-7 h-7" style={{ color: '#FFFFFF' }} strokeWidth={3} />
                  </div>
                  <p className="text-lg font-bold mb-1" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>
                    You're in
                  </p>
                  <p className="text-sm mb-5" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                    {message}
                  </p>
                  <button
                    onClick={reset}
                    className="px-6 py-2.5 rounded-full text-sm font-semibold"
                    style={{ backgroundColor: '#E57A44', color: '#FFFFFF', fontFamily: 'Manrope, sans-serif' }}
                    data-testid="subscribe-done"
                  >
                    Close
                  </button>
                </div>
              ) : (
                <form onSubmit={submit}>
                  <div className="mb-3">
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: '#2C2A29', fontFamily: 'Manrope, sans-serif' }}>Your name <span style={{ color: '#8C867D', fontWeight: 400 }}>(optional)</span></label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      maxLength={120}
                      placeholder="Friend"
                      style={inputStyle}
                      data-testid="subscribe-name"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-semibold mb-1.5" style={{ color: '#2C2A29', fontFamily: 'Manrope, sans-serif' }}>Email</label>
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      maxLength={200}
                      placeholder="your@email.com"
                      autoFocus
                      style={inputStyle}
                      data-testid="subscribe-email"
                    />
                  </div>
                  {state === 'error' && (
                    <p className="text-xs mb-3 p-2 rounded-lg" style={{ backgroundColor: '#FDECEB', color: '#8C3D34', fontFamily: 'Nunito, sans-serif' }} data-testid="subscribe-error">
                      {message}
                    </p>
                  )}
                  <button
                    type="submit"
                    disabled={state === 'sending' || !email.trim()}
                    className="w-full py-3 rounded-full text-sm font-bold transition disabled:opacity-50 hover:-translate-y-0.5"
                    style={{
                      background: 'linear-gradient(135deg, #F4D67A 0%, #E8C265 55%, #C99B2E 100%)',
                      color: '#3D2E0A',
                      fontFamily: 'Manrope, sans-serif',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      boxShadow: '0 6px 20px rgba(201,155,46,0.4), inset 0 1px 0 rgba(255,255,255,0.5)',
                      border: '1px solid rgba(255,255,255,0.5)',
                    }}
                    data-testid="subscribe-submit"
                  >
                    {state === 'sending' ? 'Sending…' : 'Subscribe'}
                  </button>
                  <p className="text-xs text-center mt-3" style={{ color: '#8C867D', fontFamily: 'Nunito, sans-serif' }}>
                    Unsubscribe any time with one click.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

const inputStyle = {
  fontFamily: 'Nunito, sans-serif',
  border: '1px solid #E6E2D8',
  borderRadius: 10,
  padding: '11px 14px',
  width: '100%',
  backgroundColor: '#FFFFFF',
  color: '#2C2A29',
  fontSize: '14px',
};
